import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { StampCard } from "@/components/StampCard";
import { RewardsHistory } from "@/components/RewardsHistory";
import { ChefHat, Loader2, Gift } from "lucide-react";
import { useT } from "@/lib/i18n";
import { LanguagePicker } from "@/components/LanguagePicker";

export default function LoyaltyCard() {
  const { t } = useT();
  const { restaurantId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("card");

  const [data, setData] = useState(null);
  const [program, setProgram] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [phone, setPhone] = useState("");
  const [signupName, setSignupName] = useState("");
  const [mode, setMode] = useState("signup");
  const [loading, setLoading] = useState(!!token);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/restaurants/${restaurantId}`).then((r) => setRestaurant(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurantId}/loyalty`).then((r) => setProgram(r.data)).catch(() => {});
    if (token) {
      api.get(`/loyalty/card/${token}`).then((r) => setData(r.data)).catch(() => setError(t("cardNotFound"))).finally(() => setLoading(false));
    }
  }, [restaurantId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/restaurants/${restaurantId}/loyalty/card`, { phone });
      setData(res);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const signup = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/restaurants/${restaurantId}/loyalty/join`, { phone, name: signupName });
      setData(res);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const accent = (data?.restaurant?.primary_color) || restaurant?.primary_color || "#D9531E";
  const prog = data?.program || program;
  const name = data?.restaurant?.name || restaurant?.name || t("restaurant");
  const logo = data?.restaurant?.logo_path || restaurant?.logo_path;

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2]">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: accent }} />
      </div>
    );

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#FAF7F2] px-5 py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 rounded-2xl overflow-hidden bg-white grid place-items-center shadow">
          {logo ? <img src={fileUrl(logo)} alt="logo" className="h-full w-full object-cover" /> : <ChefHat className="h-6 w-6" style={{ color: accent }} />}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl font-bold text-[#1F1D1B]">{name}</h1>
          <p className="text-xs text-[#6B6560] flex items-center gap-1"><Gift className="h-3 w-3" /> {t("loyaltyStampCard")}</p>
        </div>
        <LanguagePicker />
      </div>

      {prog && !prog.active ? (
        <p data-testid="loyalty-off-notice" className="bg-white rounded-2xl border border-[#E6E1DA] p-6 text-sm text-[#6B6560]">{t("loyaltyOff")}</p>
      ) : data ? (
        <div className="space-y-4 fade-up">
          {data.member.name && <p className="text-sm text-[#6B6560]">{t("hiCard", { name: data.member.name })}</p>}
          <StampCard program={data.program} member={data.member} accent={accent} />
          <RewardsHistory history={data.member.reward_history} accent={accent} />
          <p className="text-xs text-center text-[#6B6560]">{t("bookmarkStamps")}</p>
          <button
            data-testid="loyalty-view-another"
            onClick={() => { setData(null); setPhone(""); setSignupName(""); setMode("view"); }}
            className="w-full inline-flex items-center justify-center gap-2 border border-[#E6E1DA] bg-white rounded-full py-3 text-sm font-medium text-[#1F1D1B]"
          >
            {t("useDifferent")}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E6E1DA] p-6">
          <div className="grid grid-cols-2 gap-1 bg-[#F0EBE3] rounded-full p-1 mb-5">
            <button data-testid="loyalty-tab-signup" onClick={() => { setMode("signup"); setError(""); }} className={`rounded-full py-2 text-sm font-semibold transition-colors ${mode === "signup" ? "text-white" : "text-[#6B6560]"}`} style={mode === "signup" ? { backgroundColor: accent } : {}}>
              {t("signUp")}
            </button>
            <button data-testid="loyalty-tab-view" onClick={() => { setMode("view"); setError(""); }} className={`rounded-full py-2 text-sm font-semibold transition-colors ${mode === "view" ? "text-white" : "text-[#6B6560]"}`} style={mode === "view" ? { backgroundColor: accent } : {}}>
              {t("viewStamps")}
            </button>
          </div>

          {mode === "signup" ? (
            <form onSubmit={signup} className="space-y-4">
              <p className="text-sm text-[#6B6560]">{prog ? t("joinEarn", { name, reward: prog.reward_description, n: prog.stamps_required }) : t("joinRewards", { name })}</p>
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-[#6B6560]">{t("yourName")}</label>
                <input data-testid="loyalty-signup-name" value={signupName} onChange={(e) => setSignupName(e.target.value)} placeholder={t("yourName")} className="mt-1.5 w-full bg-[#FAF7F2] border border-[#E6E1DA] rounded-xl px-4 py-3 text-sm text-[#1F1D1B] focus:outline-none" />
              </div>
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-[#6B6560]">{t("phone")}</label>
                <input data-testid="loyalty-signup-phone" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" className="mt-1.5 w-full bg-[#FAF7F2] border border-[#E6E1DA] rounded-xl px-4 py-3 text-sm text-[#1F1D1B] focus:outline-none" />
              </div>
              {error && <p data-testid="loyalty-card-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
              <button data-testid="loyalty-signup-button" type="submit" disabled={submitting} className="w-full rounded-full py-3.5 font-bold text-white flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: accent }}>
                {submitting && <Loader2 className="h-5 w-5 animate-spin" />} {t("signUpView")}
              </button>
            </form>
          ) : (
            <form onSubmit={lookup} className="space-y-4">
              <p className="text-sm text-[#6B6560]">{t("enterPhoneView")}</p>
              <input data-testid="loyalty-card-phone-input" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("phone")} className="w-full bg-[#FAF7F2] border border-[#E6E1DA] rounded-xl px-4 py-3 text-sm text-[#1F1D1B] focus:outline-none" />
              {error && <p data-testid="loyalty-card-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
              <button data-testid="loyalty-card-lookup-button" type="submit" disabled={submitting} className="w-full rounded-full py-3.5 font-bold text-white flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: accent }}>
                {submitting && <Loader2 className="h-5 w-5 animate-spin" />} {t("viewMyCard")}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
